import * as React from 'react';
import classnames from 'classnames';

import MediaPreview from 'components/MediaPreview';
import { withModal } from 'components/modal/ModalHOC';

import { __t, applyTranslations } from 'utils/language';
import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';

import type { DeliverableAttachment } from 'types';

applyTranslations(require('./image-grid.i18n.json'));

type Props = {
  attachments: Array<DeliverableAttachment>,
  size: number,
  gutter?: number
};

const DivWithModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

class ImageGrid extends React.PureComponent<Props> {
  renderMediaItem(attachment: DeliverableAttachment, width: number, height: number): React.ReactNode {
    const key = `attachment-${attachment.id}`;

    return (
      <MediaPreview
        key={key}
        className="image-grid__item"
        attachment={{
          ...attachment,
          format: attachment.assetType
        }}
        width={width}
        height={height}
      />
    );
  }

  renderMoreItem(amount: number, width: number, height: number): React.ReactNode {
    return (
      <div
        key="attachments-more"
        className="image-grid__item"
      >
        <div
          className="image-grid__empty"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          {__t('image_grid.more_attachments', { amount })}
        </div>
      </div>
    );
  }

  renderMediaItemGallery(attachments: Array<DeliverableAttachment>, size: number, gutter: number): React.ReactNode {
    const halfSize = size / 2;
    const halfGutter = gutter / 2;
    const galleryItems = prepareDeliverableAttachmentsForGallery(attachments);
    const maxNumImages = 3;

    return (
      <React.Fragment>
        {
          galleryItems.slice(0, maxNumImages).map((item, index) => (
            <DivWithModal
              key={`image-grid__item-${item.assetUrl}`}
              modalContentProps={{
                items: galleryItems,
                currentPosition: index
              }}
            >
              {this.renderMediaItem(attachments[index], halfSize - halfGutter, halfSize - halfGutter)}
            </DivWithModal>
          ))
        }
        <DivWithModal
          key="image-grid__item-more"
          modalContentProps={{
            items: galleryItems,
            currentPosition: 3
          }}
        >
          <div className="campaign-deliverables-card__item-more-gallery">
            {this.renderMoreItem(attachments.length - maxNumImages, halfSize - halfGutter, halfSize - halfGutter)}
          </div>
        </DivWithModal>
      </React.Fragment>
    );
  }

  renderEmptyItem(width: number, height: number): React.ReactNode {
    return (
      <div
        key="attachments-empty"
        className="image-grid__item image-grid__empty"
        style={{ width: `${width}px`, height: `${height}px` }}
      >
        {__t('image_grid.empty_block')}
      </div>
    );
  }

  renderGrid(attachments: Array<DeliverableAttachment>, size: number, gutter: number): React.ReactNode {
    const halfSize = size / 2;
    const halfGutter = gutter / 2;

    if (attachments.length === 0) {
      return this.renderEmptyItem(size, size);
    }

    if (attachments.length === 1) {
      return this.renderMediaItem(attachments[0], size, size);
    }

    if (attachments.length === 2) {
      return [
        this.renderMediaItem(attachments[0], size, halfSize - halfGutter),
        this.renderMediaItem(attachments[1], size, halfSize - halfGutter)
      ];
    }

    if (attachments.length === 3) {
      return [
        this.renderMediaItem(attachments[0], halfSize - halfGutter, halfSize - halfGutter),
        this.renderMediaItem(attachments[1], halfSize - halfGutter, halfSize - halfGutter),
        this.renderMediaItem(attachments[2], size, halfSize - halfGutter)
      ];
    }

    if (attachments.length === 4) {
      return [
        this.renderMediaItem(attachments[0], halfSize - halfGutter, halfSize - halfGutter),
        this.renderMediaItem(attachments[1], halfSize - halfGutter, halfSize - halfGutter),
        this.renderMediaItem(attachments[2], halfSize - halfGutter, halfSize - halfGutter),
        this.renderMediaItem(attachments[3], halfSize - halfGutter, halfSize - halfGutter)
      ];
    }

    return this.renderMediaItemGallery(attachments, size, gutter);
  }

  render(): React.ReactNode {
    const { attachments, size, gutter = 0 } = this.props;

    const classes = classnames({
      'image-grid': true,
      [`image-grid--${attachments.length}`]: attachments.length
    });

    return (
      <div
        className={classes}
        style={{ gridGap: gutter }}
      >
        {
          this.renderGrid(
            attachments,
            size,
            gutter
          )
        }
      </div>
    );
  }
}

export default ImageGrid;
